import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

export const fetchSavedClientRatingAll = createAsyncThunk(
  "clientRatingall/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/clientRating");
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  }
);

export const fetchClientRatingData = createAsyncThunk(
  "clientRating/fetch",
  async ({ AssignedClient, groupId }, { rejectWithValue }) => {
    try {
      const client = String(AssignedClient ?? "").trim();
      const gid = String(groupId ?? "").trim();
      // Avoid `/clientRating//…`; list endpoint is GET `/clientRating`
      if (!client || !gid) {
        const response = await axiosInstance.get("/clientRating");
        return response.data;
      }
      const response = await axiosInstance.get(
        `/clientRating/${encodeURIComponent(client)}/${encodeURIComponent(gid)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

export const updateClientRatingData = createAsyncThunk(
  "clientRating/update",
  async (ratings, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put("/clientRating", ratings);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update data");
    }
  }
);

export const getClientRatingData = createAsyncThunk(
  "clientRating/get",
  async ({ clientName, groupName }, { rejectWithValue }) => {
    try {
      const client = String(clientName ?? "").trim();
      const group = String(groupName ?? "").trim();
      // Avoid `/clientRating//…`; list endpoint is GET `/clientRating`
      if (!client || !group) {
        const response = await axiosInstance.get("/clientRating");
        return response.data;
      }
      const response = await axiosInstance.get(
        `/clientRating/${encodeURIComponent(client)}/${encodeURIComponent(group)}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);
export const getsingleClientRatingData = createAsyncThunk(
  "clientRating/fetched",
  async (clientName, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/clientRating/getclient/${clientName}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

const clientRatingSlice = createSlice({
  name: "clientRating",
  initialState: {
    ratingDataAll: [],
    ratingData: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSavedClientRatingAll.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSavedClientRatingAll.fulfilled, (state, action) => {
        state.loading = false;
        const seen = new Set();
        const uniqueRatings = [];
        action.payload.forEach((item) => {
          const gid =
            item.groupid?._id ?? item.groupid?.id ?? item.groupid ?? "";
          const gidStr = gid != null ? String(gid).trim() : "";
          const key = `${item.clientName}::${gidStr || item.groupName || ""}`;
          if (!seen.has(key)) {
            seen.add(key);
            uniqueRatings.push({
              clientName: item.clientName,
              groupName: item.groupName,
              groupid: gidStr,
              TotalRating: item.TotalRating,
            });
          }
        });
        state.ratingDataAll = uniqueRatings;
      })
      .addCase(fetchSavedClientRatingAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchClientRatingData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientRatingData.fulfilled, (state, action) => {
        state.loading = false;
        state.ratingData = action.payload;
      })
      .addCase(fetchClientRatingData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateClientRatingData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClientRatingData.fulfilled, (state, action) => {
        state.loading = false;
        state.ratingData = action.payload;
      })
      .addCase(updateClientRatingData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getClientRatingData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClientRatingData.fulfilled, (state, action) => {
        state.loading = false;
        state.ratingData = action.payload;
      })
      .addCase(getClientRatingData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(getsingleClientRatingData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getsingleClientRatingData.fulfilled, (state, action) => {
        state.loading = false;
        state.ratingData = action.payload;
      })
      .addCase(getsingleClientRatingData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["dashboard", "clientrating"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        if (eventAction === "updated") {
          state.ratingDataAll = updateItemInList(state.ratingDataAll, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
          state.ratingData = updateItemInList(state.ratingData, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
        }

        if (eventAction === "created") {
          state.ratingDataAll = addItemToList(state.ratingDataAll, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
          state.ratingData = addItemToList(state.ratingData, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
        }

        if (eventAction === "deleted") {
          state.ratingDataAll = removeItemFromList(state.ratingDataAll, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
          state.ratingData = removeItemFromList(state.ratingData, incomingItem, {
            idKeys: ["_id", "id", "clientName"],
          });
        }
      });
  },
});

export default clientRatingSlice.reducer;
